// 드라이브(Nextcloud Files)에서 오피스 파일(엑셀·PPT·워드·csv 등)을 누르면 NC 편집기를
// «새 창(새 탭)»으로 띄운다 — 2026-10-01 마스터 지시(«새창으로», PC·휴대폰 모두).
// 🔴 NC·richdocuments 는 안 고친다. 같은 오리진 iframe 문서에 **캡처 단계** 리스너를 달아
//    NC 의 execDefaultAction(Viewer 모달)보다 먼저 가로채고, 우리가 새 탭을 연다.
// 🔴 richdocuments `/index` 라우트는 CSRF 검사 대상이다(#[NoCSRFRequired] 없음) → `requesttoken` 을
//    같이 보낸다. 토큰은 클릭 «그 순간» 문서에서 동기로 읽는다(await·fetch 를 끼우면 사용자
//    제스처가 끊겨 팝업이 차단된다). 토큰이 없으면 아예 가로채지 않는다(= 지금처럼 같은 창).
// 🔴 창이 안 열리면(팝업 차단) 기본 동작을 막지 않는다 — 기능이 사라지는 것보다 같은 창이 낫다.
//    openWindow 는 Window 또는 true(열었음) / null·false(못 엶)를 돌려준다(utils/openNoopener 는 true/false).
// 🔴 새 탭은 틀 페이지(public/office-frame.html) 안 iframe 으로 연다 — 최상위 index 는 postMessage 자기 루프로 CPU 100%+(10-06 실측).
// 전부 try/catch — NC DOM 이 바뀌면 조용히 NC 기본 동작으로 돌아간다.

// richdocuments 가 기본으로 여는 확장자(DEV Capabilities.php MIMETYPES + MIMETYPES_MSOFFICE,
// mimetypemapping.dist.json 대조 46개). 🔴 txt·md(text 앱)·pdf(pdf 뷰어)·이미지는 대상이 아니다.
export const OFFICE_EXTS = new Set([
  // 문서
  'doc', 'dot', 'docx', 'dotx', 'docm', 'odt', 'ott', 'fodt', 'rtf', 'pages', 'wpd', 'lwp',
  // 시트
  'xls', 'xla', 'xlt', 'xlsx', 'xltx', 'xlsm', 'xltm', 'xlam', 'xlsb', 'ods', 'ots', 'fods',
  'numbers', 'csv',
  // 발표
  'ppt', 'pot', 'ppa', 'pps', 'pptx', 'potx', 'ppsx', 'ppam', 'pptm', 'potm', 'ppsm',
  'odp', 'otp', 'fodp', 'key', 'keynote',
  // 그리기
  'odg', 'fodg', 'vsd', 'vsdx',
]);

export const GUARD_KEY = '__gwDriveOfficeNW';
export const ROW_SEL = 'tr[data-cy-files-list-row]';
export const NAME_SEL = '.files-list__row-name-link';
// 이름(버튼) · 아이콘 — NC 는 둘 다 execDefaultAction 으로 연다.
export const CLICK_SEL = NAME_SEL + ', .files-list__row-icon';
// «…» 메뉴의 기본 열기 항목(FileEntryActions.vue 가 action.id 를 이 속성으로 찍는다).
export const MENU_VIEW_SEL = '[data-cy-files-list-row-action="view"]';
export const ACTIVE_ROW_CLASS = 'files-list__row--active';
const FILEID_RE = /^[1-9][0-9]*$/;
// 확장자와 mime 이 엇갈린 파일은 NC 가 mime 으로 연다 → 우리도 가로채지 않는다.
const NON_OFFICE_MIMES = ['text/plain', 'text/markdown', 'application/pdf'];
const TYPING_TAGS = ['input', 'textarea', 'select'];

// 마지막 '.' 뒤 소문자. 점이 없거나 맨 앞이면('.xlsx' 단독) 확장자로 보지 않는다.
export function officeExt(name) {
  if (typeof name !== 'string') return '';
  const at = name.lastIndexOf('.');
  if (at <= 0) return '';
  return name.slice(at + 1).toLowerCase();
}

// NC CSRF 토큰 — <head data-requesttoken> 가 정본, 없으면 OC.requestToken.
export function readToken(doc) {
  if (!doc) return '';
  try {
    const head = doc.head;
    const fromHead = head && head.dataset ? head.dataset.requesttoken : '';
    if (fromHead) return String(fromHead);
    const win = doc.defaultView;
    const oc = win && win.OC;
    if (oc && oc.requestToken) return String(oc.requestToken);
  } catch (e) { /* */ }
  return '';
}

// Viewer 없이 Collabora 를 화면 전체로 띄우는 단독 페이지(richdocuments document#index).
export function editorUrl(base, fileId, token) {
  return String(base) + '/index.php/apps/richdocuments/index?fileId='
    + encodeURIComponent(fileId) + '&requesttoken=' + encodeURIComponent(token);
}

// 새 탭이 여는 주소 = 틀 페이지 + src(editorUrl). 틀이 src 를 검증한 뒤 같은 오리진 iframe 으로 싣는다.
export function framedEditorUrl(base, fileId, token, frameBase = '') {
  return frameBase + '/office-frame.html?src=' + encodeURIComponent(editorUrl(base, fileId, token));
}

function matches(el, sel) {
  try { return !!el && typeof el.matches === 'function' && el.matches(sel); } catch (e) { return false; }
}

function closestOf(el, sel) {
  try { return (el && typeof el.closest === 'function') ? el.closest(sel) : null; } catch (e) { return null; }
}

function hasModifier(ev) {
  return !!(ev.ctrlKey || ev.metaKey || ev.shiftKey || ev.altKey);
}

// 행이 «지금 새 탭으로 열어도 되는 오피스 파일» 인지. 하나라도 어긋나면 null.
export function rowTarget(tr) {
  try {
    if (!matches(tr, ROW_SEL)) return null;
    const fileId = tr.getAttribute('data-cy-files-list-row-fileid') || '';
    if (!FILEID_RE.test(fileId)) return null;
    if (!OFFICE_EXTS.has(officeExt(tr.getAttribute('data-cy-files-list-row-name')))) return null;
    // FileEntry 컴포넌트 루트라 __vue__.source(Node)가 있다 — 폴더·휴지통·엇갈린 mime 을 여기서 가른다.
    const src = tr.__vue__ && tr.__vue__.source;
    if (!src || src.type !== 'file') return null;
    if (String(src.fileid) !== fileId) return null;
    if (typeof src.root === 'string' && src.root.indexOf('/files/') !== 0) return null;
    if (typeof src.mime === 'string' && NON_OFFICE_MIMES.indexOf(src.mime) >= 0) return null;
    if (tr.querySelector('form.files-list__row-rename')) return null;        // 이름 변경 중
    if (!tr.querySelector('button' + NAME_SEL + ', [role="button"]' + NAME_SEL)) return null;  // 기본 액션 없음
    return { fileId, row: tr, viaMenu: false };
  } catch (e) { return null; }
}

// «…» 메뉴는 팝오버라 행 밖에 그려진다 → 메뉴 버튼이 펼쳐진 행이 «정확히 1개» 일 때만 그 행.
function expandedRow(doc) {
  try {
    const rows = Array.prototype.slice.call(doc.querySelectorAll(ROW_SEL));
    const open = rows.filter((tr) => {
      try { return !!tr.querySelector('td.files-list__row-actions [aria-expanded="true"]'); } catch (e) { return false; }
    });
    return open.length === 1 ? open[0] : null;
  } catch (e) { return null; }
}

function activeRow(doc) {
  try { return doc.querySelector('tr.' + ACTIVE_ROW_CLASS + '[data-cy-files-list-row]'); } catch (e) { return null; }
}

function isTypingTarget(el) {
  try {
    if (!el) return false;
    if (el.isContentEditable) return true;
    const tag = String(el.tagName || '').toLowerCase();
    return TYPING_TAGS.indexOf(tag) >= 0;
  } catch (e) { return false; }
}

// NC 핫키와 같은 조건 — 모달이 떠 있으면 Enter 는 목록 것이 아니다.
function hasOpenModal(doc) {
  try {
    const list = doc.querySelectorAll('.modal-mask');
    const win = doc.defaultView;
    for (let i = 0; i < list.length; i += 1) {
      const st = (win && typeof win.getComputedStyle === 'function') ? win.getComputedStyle(list[i]) : null;
      if (st && (st.display === 'none' || st.visibility === 'hidden')) continue;
      return true;
    }
  } catch (e) { /* */ }
  return false;
}

// 이벤트 → 열 대상. 공개 공유(/s/)·수식키·비왼쪽 버튼·입력칸·모달은 전부 제외.
export function pickTarget(ev, doc) {
  try {
    if (!ev || !doc) return null;
    let path = '';
    try { path = (doc.location && doc.location.pathname) || ''; } catch (e) { path = ''; }
    if (path.indexOf('/s/') >= 0) return null;            // 공개 공유 화면은 건드리지 않는다
    if (hasModifier(ev)) return null;                      // Ctrl/Meta+클릭 = NC 기본(이미 새 탭)
    const target = ev.target;
    if (ev.type === 'click') {
      if (ev.button !== 0) return null;                    // 가운데·우클릭 = NC 기본
      const el = closestOf(target, CLICK_SEL);
      if (el) return rowTarget(closestOf(el, ROW_SEL));
      if (!closestOf(target, MENU_VIEW_SEL)) return null;
      const found = rowTarget(expandedRow(doc));
      if (found) found.viaMenu = true;
      return found;
    }
    if (ev.type === 'keydown') {
      if (ev.key !== 'Enter') return null;
      if (isTypingTarget(target) || hasOpenModal(doc)) return null;
      // NC 도 «활성 행»(주소의 fileId = 그 행)만 연다 — 포커스가 목록 밖이면 활성 행으로 본다.
      const row = closestOf(target, ROW_SEL) || activeRow(doc);
      if (!row || !row.classList || !row.classList.contains(ACTIVE_ROW_CLASS)) return null;
      return rowTarget(row);
    }
    return null;
  } catch (e) { return null; }
}

// doc 당 1회 설치. 반환 = dispose(리스너·가드 해제). 이미 설치됐거나 드라이브 문서가 아니면 null.
// options: { isActive, openWindow(url) -> Window|true(열었음) / null|false(못 엶), base='/drive' }
export function setupDriveOfficeNewWindow(doc, options) {
  const opts = options || {};
  const base = (typeof opts.base === 'string' && opts.base) ? opts.base : '/drive';
  const isActive = (typeof opts.isActive === 'function') ? opts.isActive : () => true;
  const openWindow = (typeof opts.openWindow === 'function') ? opts.openWindow : null;
  if (!doc || !openWindow) return null;
  let win = null;
  try {
    const path = (doc.location && doc.location.pathname) || '';
    if (path.indexOf(base + '/') !== 0) return null;       // about:blank·다른 앱 문서엔 안 붙인다
    win = doc.defaultView;
  } catch (e) { return null; }
  if (!win || typeof win.addEventListener !== 'function') return null;
  // 🔴 멱등 — cleanupDriveHeader 가 같은 doc 에 수십 번 불린다. 같은 오리진 첫 이동에서 Window 가
  //    재사용될 수 있어 doc·win 양쪽에 표시한다.
  if (doc[GUARD_KEY] || win[GUARD_KEY]) return null;
  doc[GUARD_KEY] = true;
  win[GUARD_KEY] = true;
  let disposed = false;

  const handler = (ev) => {
    if (disposed) return;
    try {
      if (!isActive()) return;
      const t = pickTarget(ev, doc);
      if (!t) return;
      const token = readToken(doc);
      if (!token) return;                                  // 토큰 없음 → CSRF 로 막힌다 = 가로채지 않는다
      const w = openWindow(framedEditorUrl(base, t.fileId, token));
      if (!w) return;                                      // 팝업 차단 → 기본 동작(같은 창)을 막지 않는다
      if (w && typeof w === 'object') { try { w.opener = null; } catch (e) { /* */ } }   // Window 를 돌려주는 호출자 호환
      // 메뉴로 열었으면 그 메뉴를 닫는다(FileEntry 의 openedMenu 세터 → actionsMenuStore).
      // 🔴 Escape 를 쏘지 않는다 — 메뉴는 안 닫히고 목록 선택만 풀린다.
      if (t.viaMenu) { try { t.row.__vue__.openedMenu = false; } catch (e) { /* */ } }
      ev.preventDefault();
      if (typeof ev.stopImmediatePropagation === 'function') ev.stopImmediatePropagation();
      ev.stopPropagation();
    } catch (e) { /* NC DOM 변동 — NC 기본 동작으로 */ }
  };

  try {
    win.addEventListener('click', handler, true);           // 🔴 캡처 — NC 엘리먼트 리스너보다 먼저
    win.addEventListener('keydown', handler, true);          // 🔴 캡처 — NC 의 window 버블 핫키보다 먼저
  } catch (e) {
    try { delete doc[GUARD_KEY]; } catch (e2) { doc[GUARD_KEY] = false; }
    try { delete win[GUARD_KEY]; } catch (e2) { win[GUARD_KEY] = false; }
    return null;
  }

  return function dispose() {
    if (disposed) return;
    disposed = true;
    try {
      win.removeEventListener('click', handler, true);
      win.removeEventListener('keydown', handler, true);
    } catch (e) { /* */ }
    try { delete doc[GUARD_KEY]; } catch (e) { doc[GUARD_KEY] = false; }
    try { delete win[GUARD_KEY]; } catch (e) { win[GUARD_KEY] = false; }
  };
}
